import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import {
  calculateAge,
  DEFAULT_IMAGE_URL,
  formatExperienceDate,
} from '../../../utils/utils';
import Img from '../../../components/Img/Img';
import { capitalize } from 'lodash';
import { LockClosed16Solid } from '../../../components/Icons/LockClosed16Solid';
import InviteToApplyButton from './InviteToApplyButtton';
import useGetApplicantData from '../../../hooks/dashboard/jobs/useGetApplicantData';
import LicenseCard from '../../../components/Card/LicenseCard';
import {
  SegmentedViewControllerLite,
  SegmentedViewDataLite,
  SegmentedViewLite,
} from '../../../components/SegmentedView/SegmentedViewLite';
import ExperienceCard from '../../../components/Card/ExperienceCard';
import EducationCard from '../../../components/Card/EducationCard';
import LocumProfileSkeleton from '../../../components/Skeletons/LocumProfileSkeleton';

const segmentedControlConfData = [
  { title: 'Experience' },
  { title: 'Education' },
  { title: 'Licenses & certifications' },
];

const LocumProfile = ({
  locumId,
  resume,
}: {
  locumId: string;
  resume?: string;
}) => {
  const [isReady, setIsReady] = useState(false);

  const {
    loading,
    applicantData: locumData,
    failed,
    getApplicantData,
  } = useGetApplicantData(locumId, false);

  const handleViewCV = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    getApplicantData(locumId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locumId]);

  async function allReady(): Promise<boolean> {
    if (loading) return false;
    if (!locumData) return false;
    if (locumData.id !== locumId) return false;
    return true;
  }

  useEffect(() => {
    setIsReady(false);
    allReady().then(setIsReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, locumData, locumId]);

  if (failed || !locumId) {
    return <Try>Select a Locum to view Profile.</Try>;
  }

  if (!isReady) {
    return (
      <>
        <LocumProfileSkeleton />
      </>
    );
  }

  return (
    <>
      <BioSection>
        <BioImageArea>
          <BioImageCircle>
            <Img
              src={locumData?.profileImage ?? DEFAULT_IMAGE_URL}
              alt="profile image"
            />
          </BioImageCircle>
          <div>
            {' '}
            <BioName>
              {capitalize(locumData?.lastName ?? '')}{' '}
              {capitalize(locumData?.firstName ?? '')}
            </BioName>
            <BioOccupation>{locumData?.occupation ?? 'N/A'}</BioOccupation>
          </div>
        </BioImageArea>
        <Bio>{locumData?.bio ?? 'N/A'}</Bio>
      </BioSection>

      <BasicInformationSection>
        <BasicLabel>Basic information </BasicLabel>
        <AgeArea>
          <div>
            <Label>Age</Label>
            <DarkCell>
              {calculateAge(locumData?.birthday ?? '') || 'N/A'} years
            </DarkCell>
          </div>
          <div>
            <Label>Years of Experience</Label>
            <DarkCell>{locumData?.yearsOfExperience} years</DarkCell>
          </div>
          <div>
            <Label>Location</Label>
            <DarkCell>
              {locumData?.state}, {locumData?.country}
            </DarkCell>
          </div>

          <MobileViewAvailability>
            <Label>Availability</Label>
            <DarkCell>
              {locumData?.isAvailableForWork
                ? 'Available for work'
                : 'Not available'}
            </DarkCell>
          </MobileViewAvailability>
        </AgeArea>
        <Availability>
          <WebViewAvailability>
            <div>Availability</div>
            <DarkCell>
              {locumData?.isAvailableForWork
                ? 'Available for work'
                : 'Not available'}
            </DarkCell>
          </WebViewAvailability>
          <InviteToApplyButton
            name={`${locumData?.firstName} ${locumData?.lastName}`}
            button
            invitedUserId={locumData?.id ?? ''}
          />
          <Button
            className="purple"
            onClick={() => resume && handleViewCV(resume)}
            disabled={resume === null}
          >
            {resume ? 'View CV' : 'No locum CV'}
            <LockClosed16Solid />
          </Button>
        </Availability>
      </BasicInformationSection>

      {/*  */}
      <ExperienceSection>
        <SegmentedViewLite>
          <SegmentedViewControllerLite
            segmentedViewControllerTitle={segmentedControlConfData}
            className={'sm-header'}
          />

          <SegmentedViewDataLite>
            <div>
              <SegmentContainer className="experiences">
                {locumData?.experiences.map((data, index) => (
                  <ExperienceCard
                    key={index}
                    title={data.title}
                    companyName={data.companyName}
                    employmentType={data.employmentType}
                    location={data.location}
                    startDate={data.startDate}
                    endDate={data.endDate}
                  />
                ))}
              </SegmentContainer>
            </div>
            <div>
              <SegmentContainer>
                <EducationCard
                  school={locumData?.verification?.primaryDegreeName ?? 'N/A'}
                  license={
                    locumData?.verification?.primaryDegreeCertificate ?? 'N/A'
                  }
                  forApplicant={true}
                />
              </SegmentContainer>
            </div>
            <div>
              <SegmentContainer>
                {locumData?.verification ? (
                  <Licenses>
                    {' '}
                    <LicenseCard
                      license={locumData?.verification?.license ?? 'N/A'}
                      degree={
                        locumData?.verification?.primaryDegreeName ?? 'N/A'
                      }
                      profession={locumData?.occupation ?? 'N/A'}
                      licensedAt={
                        formatExperienceDate(
                          locumData?.verification?.yearOfCurrentLicense
                        ) ?? 'N/A'
                      }
                      forApplicant={true}
                    />
                    {locumData.experiences &&
                      locumData.experiences
                        .filter((exp) => exp.otherQualification !== null)
                        .map((exp, index) => (
                          <LicenseCard
                            key={index}
                            degree={exp.otherQualification}
                            licensedAt={formatExperienceDate(
                              exp.yearOfQualification
                            )}
                            license={exp.qualificationCertificate ?? ''}
                            forApplicant={true}
                          />
                        ))}
                  </Licenses>
                ) : !locumData?.verification && locumData?.experiences ? (
                  <Licenses>
                    {locumData.experiences
                      .filter((exp) => exp.otherQualification !== null)
                      .map((exp, index) => (
                        <LicenseCard
                          key={index}
                          degree={exp.otherQualification}
                          licensedAt={formatExperienceDate(
                            exp.yearOfQualification
                          )}
                          license={exp.qualificationCertificate ?? ''}
                          forApplicant={true}
                        />
                      ))}
                  </Licenses>
                ) : (
                  'No licenses and certificates'
                )}
              </SegmentContainer>
            </div>
          </SegmentedViewDataLite>
        </SegmentedViewLite>
      </ExperienceSection>
    </>
  );
};

export default LocumProfile;

const BioSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  grid-row: span 2;
`;

const BioImageArea = styled.div`
  display: flex;
  gap: 10px;
  font-size: 16px;
`;

const BioImageCircle = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 70px;
  width: 70px;
  overflow: hidden;
  border-radius: 50%;
  margin-bottom: 16px;
`;

const BioName = styled.div`
  display: flex;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.blackBlackMain};
`;

const BioOccupation = styled.div`
  display: flex;
  color: ${({ theme }) => theme.palette.mainBlue};
`;

const Bio = styled.div`
  display: flex;
  font-weight: 400;
  color: ${({ theme }) => theme.palette.blackBlack3};
  line-height: 24px;
  max-height: 320px;
  overflow: auto;
`;

const BasicInformationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  color: ${({ theme }) => theme.palette.greyGrey2};

  @media (max-width: 884px) {
    grid-row: 1;
    padding: 24px;
  }
`;

const BasicLabel = styled.div`
  color: ${({ theme }) => theme.palette.blackBlack3};
  font-weight: 500;
`;

const AgeArea = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
`;

const Label = styled.div`
  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Availability = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: repeat(3, 1fr);
  gap: 34px;

  @media (max-width: 884px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const MobileViewAvailability = styled.div`
  @media (min-width: 884px) {
    display: none;
  }
`;

const WebViewAvailability = styled.div`
  @media (max-width: 884px) {
    display: none;
  }
`;

const DarkCell = styled.div`
  color: ${({ theme }) => theme.palette.greyGrey1};

  @media (max-width: 884px) {
    font-size: 14px;
  }
`;

const SegmentContainer = styled.div`
  display: flex;

  &.experiences {
    flex-direction: column;
    max-height: 320px;
    margin-left: -32px;
    margin-right: -32px;
    overflow-y: auto;
  }

  @media (max-width: 480px) {
    width: 100%;
    max-height: fit-content;
    gap: 16px;

    &.experiences {
      flex-direction: column;
      margin: 0px;
    }
  }
`;

const Button = styled.button`
  display: flex;
  justify-content: space-around;
  color: white;
  font-weight: 500;
  font-size: 14px;
  height: fit-content;
  border-radius: 4px;
  padding: 10px 20px;
  background-color: ${({ theme }) => theme.palette.mainBlue};
  cursor: pointer;
  width: 10rem;
  border: none;
  white-space: nowrap;

  &.purple {
    background-color: ${({ theme }) => theme.palette.mainPurple};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: ${({ theme }) => theme.palette.greyGrey2};
  }
`;

const Try = styled.p`
  color: ${({ theme }) => theme.palette.greyGrey2};
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
`;

const Licenses = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 2rem;
  padding-top: 1rem;
`;

const ExperienceSection = styled.div`
  display: flex;
`;
